/**
 * The agent cursor and the changed-word flash: presentation-only marks on
 * the edit canvas that follow the assistant's tool steps. Module-level
 * singletons — there is one agent and one canvas, and the marks must
 * survive panel re-renders.
 */

import {
  diffWords,
  changedRanges,
} from '@interaktiv/volto-ai-editor/components/Assistant/diff';
import { collectColumnBlocks } from '@interaktiv/volto-ai-editor/components/Assistant/blockOrder';

const REORDER_MS = 450;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const cursor = {
  node: null,
  hideTimer: null,
  placeFrame: null,
  scrollTimer: null,
};

const findDraggableBlockNode = (blockId) =>
  document.querySelector(`[data-rbd-draggable-id="${blockId}"]`);

export const findBlockNode = (blockId) =>
  document.querySelector(`[data-assistant-block-id="${blockId}"]`) ||
  findDraggableBlockNode(blockId) ||
  document.getElementById(blockId);

/**
 * The columns add-on renders a Grid.Column for each column wrapper but does
 * not expose the wrapper ID in the DOM. Pair those nodes with the state order
 * so the cursor and FLIP animation can address columns like normal blocks.
 */
export const indexColumnNodes = (pageState) => {
  document
    .querySelectorAll('[data-assistant-block-id]')
    .forEach((node) => node.removeAttribute('data-assistant-block-id'));

  const indexed = new Map();
  collectColumnBlocks(pageState).forEach(({ blockId, columnIds }) => {
    const blockNode =
      findDraggableBlockNode(blockId) || document.getElementById(blockId);
    const columnsRoot = blockNode?.matches('.columns-block')
      ? blockNode
      : blockNode?.querySelector('.columns-block');
    const grid = [...(columnsRoot?.children || [])].find((node) =>
      node.classList.contains('column-grid'),
    );
    const columnNodes = [...(grid?.children || [])].filter((node) =>
      node.classList.contains('block-column'),
    );

    columnIds.forEach((columnId, index) => {
      const node = columnNodes[index];
      if (!node) return;
      node.setAttribute('data-assistant-block-id', columnId);
      indexed.set(columnId, node);
    });
  });
  return indexed;
};

const releaseNode = (node) => {
  node.classList.remove(
    'assistant-cursor',
    'assistant-entered',
    'assistant-focus',
  );
  node.classList.add('assistant-cursor-leave');
  setTimeout(() => node.classList.remove('assistant-cursor-leave'), 700);
};

const cancelPlacement = () => {
  if (cursor.placeFrame) cancelAnimationFrame(cursor.placeFrame);
  cursor.placeFrame = null;
  if (cursor.scrollTimer) clearTimeout(cursor.scrollTimer);
  cursor.scrollTimer = null;
};

export const clearCursor = () => {
  cancelPlacement();
  if (cursor.hideTimer) clearTimeout(cursor.hideTimer);
  cursor.hideTimer = null;
  if (cursor.node) releaseNode(cursor.node);
  cursor.node = null;
};

/**
 * Move the cursor onto a block. With `follow` the canvas also scrolls to
 * the block; without it, the viewport stays where the user put it.
 * `entered` plays the insert animation for blocks that did not exist
 * before this step. `transient` fades the cursor out by itself (used for
 * click-to-locate affordances).
 *
 * Placement runs on animation frames: the new form data commits before the
 * next frame paints, so entered blocks carry their marks from the first
 * painted frame (no unmarked flash), and a cancelled turn can revoke a
 * pending placement (clearCursor cancels the frame).
 */
export interface CursorOptions {
  /** Scroll the block into view once it is placed. */
  follow?: boolean;
  /** The block the agent just entered, marked from the first painted frame. */
  entered?: boolean;
  /** A placement that a cancelled turn may revoke. */
  transient?: boolean;
  /** Explicit location request: emphasize the block for five seconds. */
  focus?: boolean;
}

export const moveCursor = (
  blockId: string | null,
  { follow, entered, transient, focus }: CursorOptions = {},
) => {
  if (!blockId) return;
  cancelPlacement();
  let attempts = 0;
  const place = () => {
    cursor.placeFrame = null;
    const node = findBlockNode(blockId);
    if (!node) {
      // Portaled blocks can mount a beat later — retry a few frames.
      if (attempts < 10) {
        attempts += 1;
        cursor.placeFrame = requestAnimationFrame(place);
      }
      return;
    }
    if (cursor.hideTimer) {
      clearTimeout(cursor.hideTimer);
      cursor.hideTimer = null;
    }
    if (cursor.node && cursor.node !== node) releaseNode(cursor.node);
    node.classList.remove('assistant-cursor-leave');
    node.classList.add('assistant-cursor');
    node.classList.toggle('assistant-focus', !!focus);
    if (entered) node.classList.add('assistant-entered');
    if (follow) {
      const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
      const scroll = () => node.scrollIntoView({ behavior, block: 'center' });
      // Mid-glide the block is still offset toward its old position —
      // scrolling now would center on the wrong spot.
      if (
        node.classList.contains('assistant-reorder') ||
        (node as HTMLElement).style.transform
      ) {
        cursor.scrollTimer = setTimeout(scroll, REORDER_MS + 60);
      } else {
        scroll();
      }
    }
    cursor.node = node;
    if (transient || focus)
      cursor.hideTimer = setTimeout(clearCursor, focus ? 5000 : 1600);
  };
  cursor.placeFrame = requestAnimationFrame(place);
};

/**
 * FLIP reorder: when a step changes the block order, displaced blocks
 * glide from their old position into the new one instead of teleporting.
 * Call right before the new form data is applied: the old positions are
 * snapshotted synchronously, and once React has committed the new order
 * (next animation frame, before paint) each displaced block is offset back
 * to where it was and the offset transitions to zero. `liftId` marks the
 * block the step deliberately moved — it travels "picked up", with a
 * drag-and-drop shadow above its neighbours.
 */
const reorder = { nodes: [], timer: null, frame: null };

const clearReorder = () => {
  if (reorder.frame) cancelAnimationFrame(reorder.frame);
  reorder.frame = null;
  if (reorder.timer) clearTimeout(reorder.timer);
  reorder.timer = null;
  reorder.nodes.forEach((node) => {
    node.classList.remove('assistant-reorder', 'assistant-reorder-lift');
    node.style.transform = '';
  });
  reorder.nodes = [];
};

export const animateReorder = ({
  beforeState,
  afterState,
  blockIds,
  liftId,
}) => {
  if (prefersReducedMotion()) return;
  indexColumnNodes(beforeState);
  const before = new Map();
  blockIds.forEach((blockId) => {
    const node = findBlockNode(blockId);
    if (!node) return;
    const { left, top } = node.getBoundingClientRect();
    before.set(blockId, { left, top });
  });
  if (!before.size) return;
  reorder.frame = requestAnimationFrame(() => {
    clearReorder();
    indexColumnNodes(afterState);
    // Distant blocks would streak across the page — they slide in from
    // just offscreen in the direction they came from instead.
    const capX = window.innerWidth * 1.2;
    const capY = window.innerHeight * 1.2;
    const moving = [];
    before.forEach((position, id) => {
      const node = findBlockNode(id);
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const deltaX = position.left - rect.left;
      const deltaY = position.top - rect.top;
      if (Math.hypot(deltaX, deltaY) < 2) return;
      moving.push({
        node,
        deltaX: Math.max(-capX, Math.min(capX, deltaX)),
        deltaY: Math.max(-capY, Math.min(capY, deltaY)),
        lift: id === liftId,
      });
    });
    moving.forEach(({ node, deltaX, deltaY, lift }) => {
      node.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
      if (lift) node.classList.add('assistant-reorder-lift');
      reorder.nodes.push(node);
    });
    if (!reorder.nodes.length) return;
    // Flush the offsets so the transition starts from the old position.
    void document.body.offsetHeight; // eslint-disable-line no-unused-expressions
    reorder.nodes.forEach((node) => {
      node.classList.add('assistant-reorder');
      node.style.transform = '';
    });
    reorder.timer = setTimeout(clearReorder, REORDER_MS + 150);
  });
};

/**
 * Image cross-fade: when a step swaps a block's picture, the old rendered
 * image is snapshotted in place (a clone overlaid in document coordinates)
 * and faded out over the incoming one. Call this *before* the new state is
 * applied, while the old picture is still in the DOM.
 */
const imageFade = { node: null, timer: null };

const clearImageFade = () => {
  if (imageFade.timer) clearTimeout(imageFade.timer);
  imageFade.timer = null;
  if (imageFade.node) imageFade.node.remove();
  imageFade.node = null;
};

export const fadeImageSwap = (blockId) => {
  if (prefersReducedMotion()) return;
  const block = findBlockNode(blockId);
  const img = block?.querySelector('img');
  if (!img || !img.complete || !img.naturalWidth) return;
  clearImageFade();
  const rect = img.getBoundingClientRect();
  if (!rect.width || !rect.height) return;
  const clone = img.cloneNode(false) as HTMLElement;
  clone.className = 'assistant-image-fade';
  clone.style.left = `${rect.left + window.scrollX}px`;
  clone.style.top = `${rect.top + window.scrollY}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.height = `${rect.height}px`;
  document.body.appendChild(clone);
  imageFade.node = clone;
  imageFade.timer = setTimeout(clearImageFade, 1300);
};

/**
 * Changed-word flash: when a step rewrote a block's text, the changed words
 * light up briefly inside the block. The marks are injected into the
 * rendered DOM (the form data itself is untouched) and removed again after
 * the fade — if the block re-renders underneath us in the meantime, the
 * marks simply disappear with it.
 */
const flash = { spans: [], timer: null, pending: null };

const clearTextFlash = () => {
  if (flash.pending) clearTimeout(flash.pending);
  flash.pending = null;
  if (flash.timer) clearTimeout(flash.timer);
  flash.timer = null;
  flash.spans.forEach((span) => {
    try {
      span.replaceWith(document.createTextNode(span.textContent));
    } catch {
      /* the block was re-rendered — nothing left to restore */
    }
  });
  flash.spans = [];
};

export const flashTextChange = (blockId, before, after) => {
  if (flash.pending) clearTimeout(flash.pending);
  // Give React a beat to render the new form data first. The handle is
  // tracked so a cancelled turn revokes flashes that have not landed yet.
  flash.pending = setTimeout(() => {
    flash.pending = null;
    const node = findBlockNode(blockId);
    if (!node) return;
    clearTextFlash();
    const ranges = changedRanges(diffWords(before, after));
    if (!ranges.length) return;
    // Map the new text onto the block's rendered text nodes.
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let rendered = '';
    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      textNodes.push({ node, start: rendered.length });
      rendered += node.data;
    }
    const base = rendered.indexOf(after);
    if (base === -1) return;
    const pieces = [];
    ranges.forEach((range) => {
      textNodes.forEach(({ node: textNode, start }) => {
        const from = Math.max(base + range.start, start);
        const to = Math.min(base + range.end, start + textNode.data.length);
        if (from < to)
          pieces.push({ textNode, from: from - start, to: to - start });
      });
    });
    // Wrap back to front, so earlier offsets stay valid while nodes split.
    pieces.reverse().forEach(({ textNode, from, to }) => {
      try {
        const domRange = document.createRange();
        domRange.setStart(textNode, from);
        domRange.setEnd(textNode, to);
        const span = document.createElement('span');
        span.className = 'assistant-diff-flash';
        domRange.surroundContents(span);
        flash.spans.push(span);
      } catch {
        /* crossed an element boundary — skip this piece */
      }
    });
    if (flash.spans.length) flash.timer = setTimeout(clearTextFlash, 1800);
  }, 200);
};

/**
 * Remove every presentation mark at once — the cursor, pending and live
 * word flashes, and a lingering image-fade clone. Called when a turn is
 * cancelled or its host goes away, including any pending FLIP frame.
 */
export const clearAgentMarks = () => {
  clearReorder();
  clearCursor();
  clearTextFlash();
  clearImageFade();
};
